// Copyright 2025, University of Maryland, All Rights Reserved
import { useMemo } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Badge,
  Tooltip,
  TooltipTrigger,
  DarkTooltipContent,
  TooltipProvider,
} from '@/components/ui';
import { Pencil, Trash2, Clock, Loader2 } from 'lucide-react';
import type { SchedulerRecord } from '../api/types/scheduler';

interface SchedulerTableProps {
  schedulers: SchedulerRecord[];
  onEdit: (scheduler: SchedulerRecord) => void;
  onDelete: (scheduler: SchedulerRecord) => void;
  isLoading?: boolean;
}

const JOB_LABELS: Record<string, string> = {
  vault_connector_schedule: 'Vault connector sync',
};

export function SchedulerTable({
  schedulers,
  onEdit,
  onDelete,
  isLoading = false,
}: SchedulerTableProps) {
  const sortedSchedulers = useMemo(() => {
    return [...schedulers].sort((a, b) => {
      // Sort by updated_at descending
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    });
  }, [schedulers]);

  const formatLastRun = (lastRun?: string | null) => {
    if (!lastRun) {
      return (
        <span className="text-gray-500 italic">Never run</span>
      );
    }
    const localDate = new Date(lastRun);
    const formattedDate = format(localDate, 'MMM d, yyyy h:mm a');
    return (
      <span className="text-sm" title={formattedDate}>
        {formatDistanceToNow(localDate, { addSuffix: true })}
      </span>
    );
  };

  const formatCadence = (cadence: string) => {
    // Daily pinned to a time of day, e.g. "daily@14:30" (US/Eastern)
    if (cadence.startsWith('daily@')) {
      return `Daily at ${cadence.slice('daily@'.length)} ET`;
    }

    // Convert cadence to readable format
    const cadenceMap: Record<string, string> = {
      minutely: 'Every minute',
      hourly: 'Every hour',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
    };

    if (cadenceMap[cadence]) {
      return cadenceMap[cadence];
    }

    // Handle "every_X_units" format
    if (cadence.startsWith('every_')) {
      const parts = cadence.split('_');
      if (parts.length === 3) {
        const [, count, unit] = parts;
        return `Every ${count} ${unit}`;
      }
    }

    return cadence;
  };

  const getContextBadge = (scheduler: SchedulerRecord) => {
    if (scheduler.job_name === 'vault_connector_schedule') {
      return (
        <div className="flex flex-wrap gap-1">
          <Badge variant="outline">WS: {scheduler.ws}</Badge>
          <Badge variant="outline">FEED: {scheduler.scope}</Badge>
        </div>
      );
    }
    if (scheduler.scope) {
      return <Badge variant="outline">SCOPE: {scheduler.scope}</Badge>;
    }
    if (scheduler.ws) {
      return <Badge variant="outline">WS: {scheduler.ws}</Badge>;
    }
    if (scheduler.org) {
      return <Badge variant="outline">ORG: {scheduler.org}</Badge>;
    }
    return <Badge variant="secondary">Global</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl border border-[#e7ecf2] bg-white px-6 py-12 shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        <span className="text-[13px] text-slate-500">Loading scheduled jobs…</span>
      </div>
    );
  }

  if (schedulers.length === 0) {
    return (
      <div className="rounded-xl border border-[#e7ecf2] bg-white px-6 py-12 text-center shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
        <Clock className="mx-auto mb-2 h-8 w-8 text-slate-300" />
        <p className="text-[13.5px] font-[550] text-slate-700">No scheduled jobs found</p>
        <p className="mt-1 text-[12.5px] text-slate-400">
          Create a new scheduler to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)] [&_th]:h-9 [&_th]:bg-[#fafbfc] [&_th]:px-4 [&_th]:text-[11.5px] [&_th]:font-semibold [&_th]:text-slate-500 [&_td]:px-4 [&_td]:py-2.5 [&_td]:text-[12.5px] [&_tbody_tr:hover]:bg-slate-50">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Job Name</TableHead>
            <TableHead>Context</TableHead>
            <TableHead>Cadence</TableHead>
            <TableHead>Last Run</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedSchedulers.map((scheduler) => (
            <TableRow key={scheduler.sk}>
              <TableCell className="font-medium">
                <span>{JOB_LABELS[scheduler.job_name] ?? scheduler.job_name}</span>
                {JOB_LABELS[scheduler.job_name] && (
                  <span className="block font-mono text-xs font-normal text-muted-foreground">
                    {scheduler.job_name}
                  </span>
                )}
              </TableCell>
              <TableCell>
                {getContextBadge(scheduler)}
              </TableCell>
              <TableCell>
                <Badge variant="secondary">
                  {formatCadence(scheduler.cadence)}
                </Badge>
              </TableCell>
              <TableCell>
                {formatLastRun(scheduler.last_run)}
              </TableCell>
              <TableCell className="text-sm text-gray-600 dark:text-gray-400">
                <span title={format(new Date(scheduler.updated_at), 'MMM d, yyyy h:mm a')}>
                  {formatDistanceToNow(new Date(scheduler.updated_at), { addSuffix: true })}
                </span>
              </TableCell>
              <TableCell className="text-right">
                <TooltipProvider delayDuration={300}>
                  <div className="flex justify-end gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(scheduler)}
                        >
                          <Pencil className="h-4 w-4" />
                          <span className="sr-only">Edit scheduler</span>
                        </Button>
                      </TooltipTrigger>
                      <DarkTooltipContent>Edit scheduler</DarkTooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(scheduler)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Delete scheduler</span>
                        </Button>
                      </TooltipTrigger>
                      <DarkTooltipContent>Delete scheduler</DarkTooltipContent>
                    </Tooltip>
                  </div>
                </TooltipProvider>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
